import { type FormikProps, withFormik } from "formik";
import { noop } from "lodash";
import { useEffect, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type Dispatch } from "redux";
import ConfigurationForm from "./ConfigurationForm/ConfigurationForm";

import { RolePermissions } from "enums";
import { type RootState } from "features/Application/globaltypes/redux";
import Restricted from "features/Application/Restricted";
import { useEventCallback } from "hooks/useEventCallback";
import { bindAction, type WizardFormBase } from "interfaces";
import { type AutosaveProps, withAutosave } from "../../../../utils";
import { type ScormPayload } from "../services/types";
import { save } from "../state/slices/DetailsSlice";
import { resetState } from "../state/slices/EntityStateSlice";
import { formFieldsSelector } from "../state/selectors";
import { updateItemAction } from "../state/thunks/EntityStateThunk";
import { type EntityMode, type FormFields } from "../types/models";
import { getScormConfigurationSchema } from "../../../../utils/validationSchemas/scormValidationSchema";

export interface ConfigureProps extends WizardFormBase {
  entityId?: number;
  mode: EntityMode;
}

export type ConfigurePropsAll = ConfigureProps & FormikProps<FormFields> & PropsFromRedux & AutosaveProps;

export const Configure = (props: ConfigurePropsAll) => {
  const isEntityCreated = useMemo(() => props.id > 0 || props.isCreating, [props.id, props.isCreating]);

  const { isValid, onIsValidChange } = props;
  useEffect(() => {
    onIsValidChange?.(isValid && isEntityCreated);
  }, [isEntityCreated, isValid, onIsValidChange]);

  const saveData = useEventCallback(
    (paramValues?: FormFields) => {
      props.saveData(paramValues || props.values);
    },
    [props],
  );

  const onNext = useEventCallback(() => {
    saveData();
  }, [saveData]);

  useEffect(() => {
    props.acceptHandlers?.({
      onNext,
      onPrevious: saveData,
      onClose: props.resetEntityState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission) => (
        <ConfigurationForm
          {...props}
          mode={props.mode}
          isEntityCreated={isEntityCreated}
          disablePreventTransitionPrompt={isEntityCreated}
          disabled={props.disabled || !hasPermission}
          isReadOnlyView={props.isPurchased}
          canBePublished={props.canBePublished}
          saveData={saveData}
        />
      )}
    />
  );
};

const mapFieldsToPayload = (fields: FormFields): ScormPayload => ({
  title: fields.title.trim(),
  description: fields.description.trim(),
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const details = state.library.scorm.details;
  const entityState = state.library.scorm.entityState;
  return {
    id: entityState.entityId,
    isCreating: entityState.changingEntityState,
    hasBeenPublished: details.item.hasBeenPublished,
    isLoaded: details.isLoaded,
    isLoading: details.isLoading,
    isDraft: details.item.isDraft,
    isPurchased: details.item.isPurchased,
    formFields: formFieldsSelector(state),
    canBePublished: details.item.canBePublished,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateItem: bindAction(updateItemAction, dispatch),
  saveData: bindAction(save, dispatch),
  resetEntityState: bindAction(resetState, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<ConfigurePropsAll, FormFields, ScormPayload>({
  getInitValues: (props) => mapFieldsToPayload(props.formFields),
  stateProvider: mapFieldsToPayload,
  entityUpdater: (props) => props.updateItem,
  isDeffered: (props) => !props.isLoaded,
})(Configure);

const component = withFormik({
  validationSchema: (props: ConfigureProps & PropsFromRedux) =>
    getScormConfigurationSchema(props.mode === "create" || props.formFields.uploadedFiles.length > 0),
  enableReinitialize: true,
  mapPropsToValues: (props: ConfigureProps & PropsFromRedux): FormFields => props.formFields,
  validateOnMount: true,
  handleSubmit: noop,
})(withAutosaveComponent);

const ConnectedComponent = connector(component);
export default ConnectedComponent;
